import React, { useState } from 'react';
import {
  IconButton, Popover, Box, Typography, Tooltip, Button, Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FormatSize, Remove, Add } from '@mui/icons-material';

export const FONT_SCALE_MIN = 0.7;
export const FONT_SCALE_MAX = 2.0;
const STEP = 0.1;

export const clampFontScale = (s: number) =>
  Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, Math.round(s * 10) / 10));

/**
 * Whole-app text-size control. Scales the entire UI (text + controls together, so
 * nothing overflows) via a zoom multiplier the parent applies and persists. Lives in
 * the top bar, so it's reachable from every page.
 */
const FontSizeControl: React.FC<{ scale: number; onChange: (s: number) => void }> = ({ scale, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const pct = Math.round(scale * 100);

  return (
    <>
      <Tooltip title="Text size">
        <IconButton
          size="small"
          aria-label="Adjust text size"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            color: 'text.primary',
            backgroundColor: (t) => alpha(t.palette.primary.main, 0.08),
            '&:hover': { backgroundColor: (t) => alpha(t.palette.primary.main, 0.12) },
          }}
        >
          <FormatSize />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: 2, mt: 1 } } }}
      >
        <Box sx={{ p: 2, width: 250 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Text size</Typography>

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Tooltip title="Smaller">
              <span>
                <IconButton
                  aria-label="Decrease text size"
                  onClick={() => onChange(clampFontScale(scale - STEP))}
                  disabled={scale <= FONT_SCALE_MIN}
                  sx={{ border: 1, borderColor: 'divider' }}
                >
                  <Remove />
                </IconButton>
              </span>
            </Tooltip>

            <Box sx={{ textAlign: 'center', minWidth: 84 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 22, lineHeight: 1.1 }}>{pct}%</Typography>
              <Typography variant="caption" color="text.secondary">
                {pct === 100 ? 'Default' : pct < 100 ? 'Smaller' : 'Larger'}
              </Typography>
            </Box>

            <Tooltip title="Larger">
              <span>
                <IconButton
                  aria-label="Increase text size"
                  onClick={() => onChange(clampFontScale(scale + STEP))}
                  disabled={scale >= FONT_SCALE_MAX}
                  sx={{ border: 1, borderColor: 'divider' }}
                >
                  <Add />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Button fullWidth size="small" variant="outlined" onClick={() => onChange(1)} disabled={scale === 1}>
            Reset to default
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Scales the app's text &amp; layout. Saved for your next visit.
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export default FontSizeControl;
